import { useEffect, useMemo, useState, useCallback } from "react";
import { api, STAGES, PRIORITIES, PROJECT_SOURCES, PROJECT_COMMITMENTS, DRAWINGS_STATUSES, LOST_REASONS, inr, stageLabel, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { PriorityBadge } from "../components/Badges";
import { Plus, Download, Upload, FileSpreadsheet, KanbanSquare, Table as TableIcon } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Link, useSearchParams } from "react-router-dom";
import Attachments from "../components/Attachments";

function toCSV(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
}

const emptyProject = {
  client_name: "", city: "", project_type: "", project_value: 0, owner_id: "",
  co_owner_id: "", sales_owner_id: "", architect: "", stage: "boq_sales_order",
  next_action: "", start_date: "", due_date: "",
  priority: "medium", source: "", project_commitment: "",
  drawings_status: "yet_to_start",
  payment_status: "pending", procurement_status: "not_started",
  installation_status: "not_scheduled",
  handover_date: "", lost_reason: "", lost_notes: "",
  notes: "",
};

export default function ProjectsPage() {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ stage: "", city: "", owner_id: "", priority: "" });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyProject);
  const [editId, setEditId] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvMode, setCsvMode] = useState("replace");
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/projects", { params }).then((r) => setProjects(r.data));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("new") === "1") {
      setEditId(null); setForm(emptyProject); setOpen(true);
      setParams({});
    }
  }, [params, setParams]);

  const filtered = useMemo(() => {
    if (!q) return projects;
    const s = q.toLowerCase();
    return projects.filter((p) =>
      p.client_name?.toLowerCase().includes(s) ||
      p.city?.toLowerCase().includes(s) ||
      p.project_type?.toLowerCase().includes(s)
    );
  }, [projects, q]);

  const openNew = () => { setEditId(null); setForm(emptyProject); setOpen(true); };
  const openEdit = (p) => { setEditId(p.id); setForm({ ...p }); setOpen(true); };

  const submit = async () => {
    try {
      if (editId) {
        await api.patch(`/projects/${editId}`, form);
        toast.success("Project updated");
      } else {
        await api.post("/projects", form);
        toast.success("Project created");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const exportCSV = () => {
    const rows = filtered.map((p) => ({
      client: p.client_name, city: p.city, type: p.project_type,
      value: p.project_value, stage: p.stage, priority: p.priority,
      due: p.due_date, payment: p.payment_status,
      procurement: p.procurement_status, installation: p.installation_status,
    }));
    const blob = new Blob([toCSV(rows)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "projects.csv"; a.click();
  };

  const downloadSampleCSV = async () => {
    try {
      const res = await api.get("/admin/projects/csv/sample", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "tappav_projects_sample.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Sample CSV downloaded");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Download failed");
    }
  };

  const submitCSV = async () => {
    if (!csvFile) { toast.error("Choose a CSV file first"); return; }
    setCsvBusy(true); setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const res = await api.post(`/admin/projects/csv/upload?mode=${csvMode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(res.data);
      const { inserted = 0, deleted = 0, errors = [] } = res.data || {};
      if (errors.length) {
        toast.warning(`Imported ${inserted} row(s). ${errors.length} skipped.`);
      } else {
        toast.success(
          csvMode === "replace"
            ? `Replaced all projects. Deleted ${deleted}, imported ${inserted}.`
            : `Imported ${inserted} project(s).`
        );
      }
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Upload failed");
    } finally {
      setCsvBusy(false);
    }
  };

  const onDragStart = (e, id) => e.dataTransfer.setData("text/plain", id);
  const onDrop = async (e, stage) => {
    const id = e.dataTransfer.getData("text/plain");
    await api.patch(`/projects/${id}`, { stage });
    toast.success("Stage updated");
    load();
  };

  const userName = (uid) => users.find((u) => u.id === uid)?.name || "Unassigned";

  return (
    <div className="space-y-4" data-testid="projects-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pipeline</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Projects</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={exportCSV} data-testid="export-projects-btn"><Download className="h-4 w-4 mr-2" />CSV</Button>
          {isAdmin && (
            <>
              <Button variant="outline" onClick={downloadSampleCSV} data-testid="download-sample-csv-btn">
                <FileSpreadsheet className="h-4 w-4 mr-2" />Sample CSV
              </Button>
              <Button variant="outline" onClick={() => { setCsvFile(null); setCsvResult(null); setCsvMode("replace"); setCsvOpen(true); }} data-testid="open-upload-csv-btn">
                <Upload className="h-4 w-4 mr-2" />Upload CSV
              </Button>
            </>
          )}
          <Button onClick={openNew} data-testid="new-project-btn"><Plus className="h-4 w-4 mr-2" />New Project</Button>
        </div>
      </div>

      <Card className="p-4 bg-card border-border">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Input placeholder="Search client, city, type…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="projects-search" />
          <Select value={filters.stage || "all"} onValueChange={(v) => setFilters({ ...filters, stage: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-stage"><SelectValue placeholder="Stage" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All stages</SelectItem>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.priority || "all"} onValueChange={(v) => setFilters({ ...filters, priority: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.owner_id || "all"} onValueChange={(v) => setFilters({ ...filters, owner_id: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All owners</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="City" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} />
        </div>
      </Card>

      <Tabs defaultValue="table" className="w-full">
        <TabsList>
          <TabsTrigger value="table" data-testid="tab-table"><TableIcon className="h-4 w-4 mr-2" />Table</TabsTrigger>
          <TabsTrigger value="kanban" data-testid="tab-kanban"><KanbanSquare className="h-4 w-4 mr-2" />Kanban</TabsTrigger>
        </TabsList>

        <TabsContent value="kanban">
          <div className="flex gap-3 overflow-x-auto pb-4">
            {STAGES.map((s) => {
              const items = filtered.filter((p) => p.stage === s.key);
              return (
                <div key={s.key} className="stage-column bg-card/60 border border-border rounded p-3"
                     onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, s.key)}
                     data-testid={`kanban-col-${s.key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</div>
                    <span className="text-xs text-muted-foreground font-mono">{items.length}</span>
                  </div>
                  <div className="space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto">
                    {items.map((p) => (
                      <div key={p.id} draggable onDragStart={(e) => onDragStart(e, p.id)}
                           onClick={() => openEdit(p)}
                           className="p-3 rounded border border-border bg-secondary/40 cursor-pointer card-hover"
                           data-testid={`project-card-${p.id}`}>
                        <div className="text-sm font-semibold truncate">{p.client_name}</div>
                        <div className="text-xs text-muted-foreground truncate">{p.project_type} · {p.city}</div>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-xs font-mono text-primary">{inr(p.project_value)}</span>
                          <PriorityBadge value={p.priority} />
                        </div>
                        {p.next_action && <div className="mt-2 text-xs text-muted-foreground truncate">→ {p.next_action}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="table">
          <Card className="bg-card border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Priority</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => openEdit(p)} data-testid={`project-row-${p.id}`}>
                    <TableCell className="font-medium">{p.client_name}</TableCell>
                    <TableCell>{p.city}</TableCell>
                    <TableCell className="text-muted-foreground">{p.project_type}</TableCell>
                    <TableCell><span className="text-xs">{stageLabel(p.stage)}</span></TableCell>
                    <TableCell className="font-mono text-primary">{inr(p.project_value)}</TableCell>
                    <TableCell>{userName(p.owner_id)}</TableCell>
                    <TableCell className="font-mono text-xs">{p.due_date || "—"}</TableCell>
                    <TableCell><PriorityBadge value={p.priority} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-border" data-testid="project-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Client Name" required>
              <Input placeholder="e.g. Kapoor Residence, Bandra" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="project-client-input" />
            </Field>
            <Field label="City">
              <Input placeholder="Mumbai" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Field>
            <Field label="Project Type">
              <Input placeholder="Home Cinema, Automation..." value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} />
            </Field>
            <Field label="Project Value (₹)">
              <Input type="number" placeholder="e.g. 4200000" value={form.project_value} onChange={(e) => setForm({ ...form, project_value: Number(e.target.value) })} />
            </Field>
            <Field label="Source">
              <Select value={form.source || "none"} onValueChange={(v) => setForm({ ...form, source: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="project-source"><SelectValue placeholder="How did we get this lead?" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{PROJECT_SOURCES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Architect / Interior Designer">
              <Input placeholder="Studio Meraki, Ar. Kunal Jain..." value={form.architect} onChange={(e) => setForm({ ...form, architect: e.target.value })} />
            </Field>
            <Field label="Project Owner" hint="Engineer running the project">
              <Select value={form.owner_id || "none"} onValueChange={(v) => setForm({ ...form, owner_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Co-Owner" hint="Additional engineer with access">
              <Select value={form.co_owner_id || "none"} onValueChange={(v) => setForm({ ...form, co_owner_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Sales Owner" hint="Who closed the sale">
              <Select value={form.sales_owner_id || "none"} onValueChange={(v) => setForm({ ...form, sales_owner_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Project Stage" hint="Current stage in pipeline">
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="project-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Project Commitment" hint="Confirmed commitment level">
              <Select value={form.project_commitment || "none"} onValueChange={(v) => setForm({ ...form, project_commitment: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="project-commitment"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not yet</SelectItem>{PROJECT_COMMITMENTS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Project Start Date">
              <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </Field>
            <Field label="Target Handover Date">
              <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </Field>
            <Field label="Drawings Status">
              <Select value={form.drawings_status || "yet_to_start"} onValueChange={(v) => setForm({ ...form, drawings_status: v })}>
                <SelectTrigger data-testid="project-drawings"><SelectValue /></SelectTrigger>
                <SelectContent>{DRAWINGS_STATUSES.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Payment Status">
              <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Procurement Status">
              <Select value={form.procurement_status} onValueChange={(v) => setForm({ ...form, procurement_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">Not started</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="ordered">Ordered</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Installation Status">
              <Select value={form.installation_status} onValueChange={(v) => setForm({ ...form, installation_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_scheduled">Not scheduled</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {(form.stage === "completed" || form.stage === "amc_service") && (
              <Field label="Handover Date" hint="Auto-filled when moved to Completed">
                <Input type="date" value={form.handover_date || ""} onChange={(e) => setForm({ ...form, handover_date: e.target.value })} data-testid="project-handover-date" />
              </Field>
            )}
            {form.stage === "lost" && (
              <Field label="Lost Reason" required>
                <Select value={form.lost_reason || ""} onValueChange={(v) => setForm({ ...form, lost_reason: v })}>
                  <SelectTrigger data-testid="project-lost-reason"><SelectValue placeholder="Why did we lose this?" /></SelectTrigger>
                  <SelectContent>{LOST_REASONS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            {form.stage === "lost" && (
              <div className="col-span-2">
                <Field label="Lost Notes" hint="Any context — competitor name, quote gap, follow-up plan">
                  <Textarea value={form.lost_notes || ""} onChange={(e) => setForm({ ...form, lost_notes: e.target.value })} data-testid="project-lost-notes" />
                </Field>
              </div>
            )}
            <div className="col-span-2">
              <Field label="Next Action">
                <Input placeholder="Follow-up call, share BOQ v3..." value={form.next_action || ""} onChange={(e) => setForm({ ...form, next_action: e.target.value })} />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label="Notes">
                <Textarea placeholder="Internal notes, context, next steps..." value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
            </div>
          </div>
          {editId && (
            <div className="pt-3 border-t border-border">
              <Attachments kind="project" parentId={editId} />
            </div>
          )}
          <div className="flex justify-between items-center pt-2">
            {editId && <Link to={`/projects/${editId}`} className="text-xs text-primary hover:underline">Open activity timeline →</Link>}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="project-save-btn">{editId ? "Save" : "Create"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="csv-upload-dialog">
          <DialogHeader><DialogTitle>Bulk Import Projects via CSV</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Download the sample CSV first, fill in your live projects, then upload it here.
              Owner columns accept team-member <span className="text-foreground">emails</span> (they will be mapped to users automatically).
            </div>

            <div className="rounded border border-border bg-secondary/30 p-3 space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Import mode</div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="csv-mode" value="replace" checked={csvMode === "replace"}
                       onChange={() => setCsvMode("replace")} className="mt-1" data-testid="csv-mode-replace" />
                <div>
                  <div className="text-sm font-medium">Replace all projects <span className="text-primary">(recommended for first-time import)</span></div>
                  <div className="text-xs text-muted-foreground">Deletes every existing project and imports the CSV as the new source of truth.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="csv-mode" value="append" checked={csvMode === "append"}
                       onChange={() => setCsvMode("append")} className="mt-1" data-testid="csv-mode-append" />
                <div>
                  <div className="text-sm font-medium">Append only</div>
                  <div className="text-xs text-muted-foreground">Keeps existing projects and adds the CSV rows on top.</div>
                </div>
              </label>
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">CSV File</label>
              <Input type="file" accept=".csv,text/csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                     className="mt-1" data-testid="csv-file-input" />
              {csvFile && <div className="text-xs text-muted-foreground mt-1">Selected: {csvFile.name} ({Math.round(csvFile.size / 1024)} KB)</div>}
            </div>

            {csvResult && (
              <div className="rounded border border-border bg-secondary/30 p-3 text-sm space-y-1" data-testid="csv-result">
                <div><span className="text-muted-foreground">Mode:</span> {csvResult.mode}</div>
                {csvResult.mode === "replace" && (
                  <div><span className="text-muted-foreground">Deleted existing:</span> {csvResult.deleted}</div>
                )}
                <div><span className="text-muted-foreground">Imported:</span> {csvResult.inserted}</div>
                {csvResult.errors?.length > 0 && (
                  <div className="pt-2">
                    <div className="text-amber-400 text-xs font-semibold mb-1">Skipped rows ({csvResult.errors.length}):</div>
                    <ul className="text-xs text-muted-foreground max-h-32 overflow-y-auto space-y-0.5">
                      {csvResult.errors.map((er, i) => (
                        <li key={i}>Row {er.row}: {er.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center pt-3 border-t border-border">
            <Button variant="outline" size="sm" onClick={downloadSampleCSV} data-testid="dialog-download-sample-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />Download sample
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCsvOpen(false)}>Close</Button>
              <Button onClick={submitCSV} disabled={csvBusy || !csvFile} data-testid="csv-submit-btn">
                {csvBusy ? "Importing…" : csvMode === "replace" ? "Replace & Import" : "Append Import"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10.5px] text-muted-foreground/70 mt-1">{hint}</div>}
    </div>
  );
}
